<template>
	<ViewLayout>
		<template #header-title>
			File Loader
		</template>
		<template #content>
			<div class="cassie-vertical-lg pb-2">
				<!-- Filter Bar -->
				<SectionCard class="filter-section">
					<template #body>
						<div class="filter-bar">
							<div class="filter-inputs">
								<DateRangeSelector
									:start-date="fromDate"
									:end-date="toDate"
									:period="selectedPeriod"
									button-class="filter-date-range"
									@apply="onDateRangeApply"
								/>
								<Dropdown
									v-model="fileType"
									label="File Type"
									:items="fileTypeOptions"
									class="filter-dropdown"
									@change="applyFilters"
								/>
							</div>
						</div>
					</template>
				</SectionCard>

				<!-- Summary Metrics -->
				<div class="metrics-grid">
					<template v-if="loading">
						<v-skeleton-loader
							v-for="n in 4"
							:key="n"
							type="card"
							height="120"
						/>
					</template>
					<template v-else>
						<MetricCard
							title="Today's Files"
							:value="todaysFilesCount"
							subtitle="Files processed today"
							icon="mdi-file-upload"
							icon-color="grey"
						/>
						<MetricCard
							title="Failed Files"
							:value="failedFilesCount"
							:danger-value="failedFilesCount > 0"
							subtitle="Requiring attention"
							icon="mdi-alert-circle"
							:icon-color="failedFilesCount > 0 ? 'error' : 'grey'"
							:border-color="failedFilesCount > 0 ? 'error' : ''"
							:highlighted="failedFilesCount > 0"
							highlight-color="error"
						/>
						<MetricCard
							title="Rows Accepted"
							:value="totalRowsAccepted.toLocaleString()"
							subtitle="Across all files"
							icon="mdi-check-circle"
							icon-color="success"
						/>
						<MetricCard
							title="Rows Rejected"
							:value="totalRowsRejected.toLocaleString()"
							:danger-value="totalRowsRejected > 0"
							subtitle="Across all files"
							icon="mdi-close-circle"
							:border-color="totalRowsRejected > 0 ? 'error' : 'grey'"
							:icon-color="totalRowsRejected > 0 ? 'error' : 'grey'"
						/>
					</template>
				</div>

				<!-- Table View -->
				<SectionCard class="table-section">
					<template #body>
						<FileLoaderTableView
							:files="fileJobs"
							:loading="loading"
							@view-details="openDetailsSheet"
						/>
					</template>
				</SectionCard>
			</div>

			<!-- Details Drawer -->
			<FileLoaderDetailsSheet
				v-if="selectedFileLoaderGuid"
				:is-open="showDetailsSheet"
				:file-loader-guid="selectedFileLoaderGuid"
				@close="closeDetailsSheet"
			/>
		</template>
	</ViewLayout>
</template>

<script>
import ViewLayout from '../../../../../shared/layouts/view-layout.vue'
import SectionCard from '../../../../../shared/components/section-card.vue'
import DateRangeSelector from '../../../../../shared/components/date-range-selector.vue'
import Dropdown from '../../../../../shared/components/dropdown.vue'
import { FileLoaderTableView, MetricCard } from '../../component-library-wrapper.js'
import FileLoaderDetailsSheet from './file-loader-details-sheet.vue'
import { getFileLoaderJobs } from '../../../../../shared/utils/api/file-loader.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import { Periods } from '../../../../../shared/constants/periods.js'

export default {
	components: {
		ViewLayout,
		SectionCard,
		DateRangeSelector,
		Dropdown,
		FileLoaderTableView,
		FileLoaderDetailsSheet,
		MetricCard
	},
	setup () {
		return { showSnackbar }
	},
	data () {
		// Calculate default date range (last 30 days)
		const today = new Date()
		const thirtyDaysAgo = new Date(today)
		thirtyDaysAgo.setDate(today.getDate() - 30)

		return {
			fileJobs: [],
			loading: false,
			// Filter state
			fromDate: thirtyDaysAgo.toISOString().split('T')[0],
			toDate: today.toISOString().split('T')[0],
			selectedPeriod: Periods.LastMonth,
			fileType: 'All',
			fileTypeOptions: [
				'All',
				'Contacts & Preferences',
				'Delete',
				'RTBF',
				'Merge'
			],
			// Drawer state
			showDetailsSheet: false,
			selectedFileLoaderGuid: null
		}
	},
	computed: {
		todaysFilesCount () {
			// Compare dates in local timezone
			const today = new Date()
			today.setHours(0, 0, 0, 0)
			const tomorrow = new Date(today)
			tomorrow.setDate(tomorrow.getDate() + 1)

			return this.fileJobs.filter(job => {
				if (!job.loadedDate) return false
				const jobDate = new Date(job.loadedDate)
				return jobDate >= today && jobDate < tomorrow
			}).length
		},
		failedFilesCount () {
			// Case-insensitive status check
			return this.fileJobs.filter(job => job.loadStatus?.toLowerCase() === 'failed').length
		},
		totalRowsAccepted () {
			return this.fileJobs.reduce((sum, job) => {
				const rows = parseInt(job.rowsAccepted, 10)
				return sum + (isNaN(rows) ? 0 : rows)
			}, 0)
		},
		totalRowsRejected () {
			return this.fileJobs.reduce((sum, job) => {
				const rows = parseInt(job.rowsRejected, 10)
				return sum + (isNaN(rows) ? 0 : rows)
			}, 0)
		}
	},
	async created () {
		await this.loadData()
	},
	methods: {
		async loadData () {
			this.loading = true
			try {
				// Normalize fileType - pass null instead of 'All' so backend receives no filter
				const fileTypeParam = this.fileType === 'All' ? null : this.fileType
				const jobsResponse = await getFileLoaderJobs({
					fromDate: this.fromDate,
					toDate: this.toDate,
					fileType: fileTypeParam
				})

				// API returns { data: [...] }, axios unwraps to response.data
				this.fileJobs = jobsResponse.data.data ?? []
			} catch (err) {
				const message = err.response?.data?.message || 'Failed to load file loader data'
				this.showSnackbar(message, 'error')
				console.error('FileLoader fetch error:', err)
			} finally {
				this.loading = false
			}
		},
		onDateRangeApply ({ period, startDate, endDate }) {
			this.selectedPeriod = period
			this.fromDate = startDate
			this.toDate = endDate
			this.applyFilters()
		},
		async applyFilters () {
			await this.loadData()
		},
		openDetailsSheet (fileLoaderGuid) {
			this.selectedFileLoaderGuid = fileLoaderGuid
			this.showDetailsSheet = true
		},
		closeDetailsSheet () {
			this.showDetailsSheet = false
			this.selectedFileLoaderGuid = null
		}
	}
}
</script>

<style scoped>
.filter-section {
	margin-bottom: 24px;
}

.filter-bar {
	/* padding: 8px 0; */
	margin-bottom: 0 !important
}

.filter-inputs {
	display: flex;
	gap: 16px;
	align-items: flex-end;
}

.filter-dropdown {
	width: 240px;
}

@media (max-width: 768px) {
	.filter-inputs {
		flex-wrap: wrap;
	}

	.filter-dropdown {
		flex: 1;
		min-width: 150px;
	}
}

@media (max-width: 480px) {
	.filter-dropdown {
		width: 100%;
	}
}

.metrics-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 16px;
	margin-bottom: 24px;
}

@media (max-width: 768px) {
	.metrics-grid {
		grid-template-columns: 1fr;
	}
}
</style>
